import React, { useCallback, useEffect, useState } from "react";
import { Plus, Search, BookOpen, FileText, FlaskConical, Filter } from "lucide-react";
import { PageHeader, Button, Input, Select, Card, CardContent, Modal, LoadingBlock, EmptyState } from "@/components/ds";
import { api, formatApiError, type KnowledgeEntry, type KnowledgeCategory } from "@/lib/api";
import { FALLBACK_CATEGORIES } from "./knowledge/constants";
import { EntryForm } from "./knowledge/EntryForm";
import { EntryRow } from "./knowledge/EntryRow";
import { Documents } from "./knowledge/Documents";
import { RetrievalTest } from "./knowledge/RetrievalTest";

type Tab = "entries" | "documents" | "retrieval";

const TABS: { key: Tab; label: string; icon: typeof BookOpen }[] = [
  { key: "entries", label: "Knowledge entries", icon: BookOpen },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "retrieval", label: "Retrieval test", icon: FlaskConical },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "archived", label: "Archived" },
];

function EntriesPanel({ categories }: { categories: KnowledgeCategory[] }) {
  const [entries, setEntries] = useState<KnowledgeEntry[] | null>(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<KnowledgeEntry | null>(null);

  const load = useCallback(async () => {
    try {
      const params: { category?: string; status?: string; q?: string } = {};
      if (category !== "all") params.category = category;
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      setEntries(await api.kbList(params));
    } catch (e) { formatApiError(e); }
  }, [category, status, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const catOptions = [{ value: "all", label: "All categories" }, ...(categories.length ? categories : FALLBACK_CATEGORIES)];

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (e: KnowledgeEntry) => { setEditing(e); setModalOpen(true); };
  const onSaved = () => { setModalOpen(false); load(); };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search knowledge…" className="pl-9" data-testid="kb-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <div className="w-44"><Select value={category} onChange={setCategory} options={catOptions} testid="kb-filter-category" /></div>
          <div className="w-40"><Select value={status} onChange={setStatus} options={STATUS_OPTIONS} testid="kb-filter-status" /></div>
        </div>
        <Button onClick={openNew} data-testid="kb-add-entry"><Plus className="h-4 w-4" /> Add entry</Button>
      </div>

      {entries === null ? (
        <LoadingBlock rows={4} />
      ) : entries.length === 0 ? (
        <EmptyState icon={BookOpen} title="No knowledge entries yet"
          description="Add the approved answers your AI can rely on when talking to customers."
          action={<Button onClick={openNew} data-testid="kb-empty-add"><Plus className="h-4 w-4" /> Add your first entry</Button>} />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="kb-entries-list">
          {entries.map((e) => <EntryRow key={e.id} entry={e} onChanged={load} onEdit={openEdit} />)}
        </CardContent></Card>
      )}

      <Modal open={modalOpen} onOpenChange={setModalOpen} title={editing ? "Edit knowledge entry" : "Add knowledge entry"} description="Approved answers your AI is allowed to use with customers." testid="kb-entry-modal">
        <EntryForm entry={editing} categories={categories} onSaved={onSaved} onCancel={() => setModalOpen(false)} />
      </Modal>
    </div>
  );
}

export default function Knowledge() {
  const [tab, setTab] = useState<Tab>("entries");
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);

  useEffect(() => { api.kbCategories().then(setCategories).catch(() => setCategories([])); }, []);

  return (
    <div data-testid="page-knowledge" className="space-y-6">
      <PageHeader title="Knowledge base" description="Approved facts your AI is allowed to use — plus documents for your records." testid="knowledge-header" />

      <div className="flex gap-1 border-b border-slate-200" data-testid="kb-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`kb-tab-${key}`}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "entries" && <EntriesPanel categories={categories} />}
      {tab === "documents" && <Documents />}
      {tab === "retrieval" && <RetrievalTest />}
    </div>
  );
}
